import { useEffect, useState } from 'react';
import { Alert, Button, Form, Modal, Spinner } from 'react-bootstrap';
import { authService } from '@/services/authService';
import {
  cancelReauth,
  finishReauth,
  isReauthRequested,
  onReauthChange,
  ReauthRestart,
} from '@/services/session/reauth';

/**
 * Asks the person for their password and authenticator code when a
 * destructive operation needs a sign-in from the last hour (TASK-649). The
 * operation runs as soon as they confirm. Mounted only while signed in.
 */
export default function ReauthDialog() {
  const [show, setShow] = useState(isReauthRequested);
  const [step, setStep] = useState<'password' | 'code'>('password');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(
    () =>
      onReauthChange(() => {
        const open = isReauthRequested();
        setShow(open);
        if (open) {
          setStep('password');
          setPassword('');
          setCode('');
          setError(null);
        }
      }),
    []
  );

  const close = () => {
    authService.cancelConfirm();
    setPassword('');
    setCode('');
    cancelReauth();
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      if (step === 'password') {
        const next = await authService.confirmPassword(password);
        setPassword('');
        if (next === 'code') {
          setStep('code');
          return;
        }
      } else {
        await authService.confirmCode(code.trim());
      }
      setCode('');
      finishReauth();
    } catch (err) {
      if (err instanceof ReauthRestart) {
        setStep('password');
        setCode('');
      }
      setError(err instanceof Error ? err.message : 'That did not work. Try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal show={show} onHide={close} backdrop="static" centered aria-labelledby="reauth-title">
      <Form onSubmit={submit}>
        <Modal.Header closeButton>
          <Modal.Title id="reauth-title">Confirm it is you</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted">
            Deploys, password resets, changes to portal users, config deletes and system-user passwords need a sign-in
            from the last hour. The portal does what you asked as soon as you confirm.
          </p>
          {error && <Alert variant="danger">{error}</Alert>}
          {step === 'password' ? (
            <Form.Group controlId="reauth-password">
              <Form.Label>Password</Form.Label>
              <Form.Control
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoFocus
                required
              />
            </Form.Group>
          ) : (
            <Form.Group controlId="reauth-code">
              <Form.Label>Code from your authenticator app</Form.Label>
              <Form.Control
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]{6}"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value)}
                autoFocus
                required
              />
            </Form.Group>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={close} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={busy}>
            {busy && <Spinner animation="border" size="sm" className="me-2" />}
            {step === 'password' ? 'Continue' : 'Confirm'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
